import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BookOpen, Microscope, Calculator, Globe } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Academics = () => {
    useEffect(() => {
        const sections = document.querySelectorAll('.animate-section');
        sections.forEach((section) => {
            gsap.fromTo(
                section,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 80%',
                        toggleActions: 'play none none reverse',
                    },
                }
            );
        });
    }, []);

    const programs = [
        {
            icon: BookOpen,
            title: 'Primary Education (K.G. - Class 5)',
            description: 'Foundation building with focus on literacy, numeracy, and creativity through play-based learning.',
            subjects: ['English', 'Mathematics', 'Science', 'Social Studies', 'Hindi', 'Computer', 'Arts & Crafts', 'Physical Education'],
        },
        {
            icon: Microscope,
            title: 'Secondary Education (Class 6 - 10)',
            description: 'Comprehensive curriculum preparing students for board examinations with practical learning.',
            subjects: ['English', 'Mathematics', 'Science (Physics, Chemistry, Biology)', 'Social Science', 'Hindi', 'Computer Science', 'Physical Education'],
        },
        {
            icon: Calculator,
            title: 'Senior Secondary - Science',
            description: 'Advanced science stream for students aspiring for engineering and medical fields.',
            subjects: ['Physics', 'Chemistry', 'Mathematics/Biology', 'English', 'Computer Science/Physical Education'],
        },
        {
            icon: Globe,
            title: 'Senior Secondary - Commerce & Arts',
            description: 'Specialized streams for students interested in business, economics, humanities, and social sciences.',
            commerce: ['Accountancy', 'Business Studies', 'Economics', 'Mathematics/Computer Science', 'English'],
            arts: ['History', 'Political Science', 'Economics', 'Geography/Psychology', 'English'],
        },
    ];

    return (
        <div className="min-h-screen pt-24" data-testid="academics-page">
            {/* Hero Section */}
            <section
                className="relative py-20 overflow-hidden"
                style={{
                    backgroundImage: `url(https://images.unsplash.com/photo-1719159381916-062fa9f435a6?w=1920)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">Academics</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">
                            Comprehensive curriculum designed for holistic development
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Programs */}
            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl mb-4 text-black">Our Programs</h2>
                        <p className="font-dm-sans text-lg text-slate-600 max-w-2xl mx-auto">
                            From kindergarten to higher secondary, we offer a complete educational journey
                        </p>
                    </div>
                    <div className="space-y-12">
                        {programs.map((program, index) => (
                            <motion.div
                                key={index}
                                className="bg-red-600 rounded-3xl p-8 my-8 md:p-12"
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                data-testid={`program-card-${index}`}
                            >
                                <div className="flex items-start gap-6">
                                    <program.icon className="w-16 h-16 text-primary shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-outfit font-bold text-2xl md:text-3xl text-primary mb-3">
                                            {program.title}
                                        </h3>
                                        <p className="font-dm-sans text-lg text-white mb-6">{program.description}</p>
                                        {program.subjects && (
                                            <div>
                                                <h4 className="font-outfit font-semibold text-lg mb-3 text-white">Subjects Offered:</h4>
                                                <div className="flex flex-wrap gap-3">
                                                    {program.subjects.map((subject, i) => (
                                                        <span
                                                            key={i}
                                                            className="px-4 py-2 bg-red text-white rounded-full text-sm font-medium border border-border border-white"
                                                        >
                                                            {subject}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {program.commerce && (
                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <h4 className="font-outfit font-semibold text-lg mb-3">Commerce Stream:</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {program.commerce.map((subject, i) => (
                                                            <span
                                                                key={i}
                                                                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium border border-border"
                                                            >
                                                                {subject}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-outfit font-semibold text-lg mb-3">Arts Stream:</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {program.arts.map((subject, i) => (
                                                            <span
                                                                key={i}
                                                                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium border border-border"
                                                            >
                                                                {subject}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Examination System */}
            <section className="py-24 bg-red-600 animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="font-outfit font-bold text-4xl text-primary text-center mb-12">
                            Examination & Assessment
                        </h2>
                        <div className="bg-white rounded-3xl p-8 md:p-12 space-y-6">
                            <div>
                                <h3 className="font-outfit text-black font-bold text-xl text-red-600 mb-3">Continuous Assessment</h3>
                                <p className="font-dm-sans text-slate-600 leading-relaxed">
                                    Regular tests, assignments, and projects to monitor student progress throughout the academic year.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-outfit text-black font-bold text-xl text-red-600 mb-3">Board Examinations</h3>
                                <p className="font-dm-sans text-slate-600 leading-relaxed">
                                    Students appear for state board examinations at the end of Class 10 and Class 12, achieving consistent 100% pass rates.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-outfit text-black font-bold text-xl text-red-600 mb-3">Modern Assessment Methods</h3>
                                <p className="font-dm-sans text-slate-600 leading-relaxed">
                                    Integration of technology with audio-visual systems, digital assessments, and practical evaluations to ensure comprehensive learning.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Academics;